import { useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { Menu, LogIn, LogOut, LayoutDashboard } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../ui/sheet";
import { Button } from "../ui/button";
import { Logo } from "../Logo";
import { useAuth } from "../../context/AuthContext";
import { ROLE_HOME } from "../../lib/labels";
import { SJI_WA_DISPLAY, SJI_WA_LINK, WaButton } from "./ContactSJI";

export const PUBLIC_NAV = [
  { to: "/", label: "Beranda", id: "home", end: true },
  { to: "/peluang-investasi", label: "Rencana Kebun Tersedia", id: "opportunities" },
  { to: "/peta-lahan", label: "Peta Lahan", id: "farm-map" },
  { to: "/kelompok-tani", label: "Kelompok / Koperasi Tani", id: "farmer-groups" },
  { to: "/cara-kerja", label: "Cara Pendanaan Kebun", id: "how-it-works" },
  { to: "/asumsi-bagi-hasil", label: "Asumsi Bagi Hasil", id: "asumsi-bagi-hasil" },
  { to: "/monitoring-kebun", label: "Monitoring Kebun", id: "monitoring" },
  { to: "/offtake-pt-sji", label: "PT SJI Offtake", id: "offtake" },
];

const MOBILE_GROUPS = [
  ["Mulai", [{ to: "/ajukan-lahan", label: "Ajukan Lahan", id: "apply-land" }, { to: "/daftar-pengelola", label: "Daftar Pengelola", id: "register-manager" },
    { to: "/login", label: "Menjadi Pemodal Kebun", id: "become-investor" }]],
  ["Informasi", [{ to: "/", label: "Beranda", id: "home", end: true }, { to: "/peluang-investasi", label: "Rencana Kebun Tersedia", id: "opportunities" },
    { to: "/peta-lahan", label: "Peta Lahan", id: "farm-map" }, { to: "/cara-kerja", label: "Cara Pendanaan & Cara Kerja", id: "how-it-works" },
    { to: "/asumsi-bagi-hasil", label: "Asumsi Bagi Hasil", id: "asumsi-bagi-hasil" },
    { to: "/kelompok-tani", label: "Pengelola", id: "farmer-groups" }, { to: "/monitoring-kebun", label: "Monitoring Kebun", id: "monitoring" },
    { to: "/offtake-pt-sji", label: "Offtake PT SJI", id: "offtake" }, { to: "/berita", label: "Berita", id: "news" }]],
];

const linkCls = ({ isActive }) =>
  `px-2.5 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${isActive ? "text-emerald-800 bg-emerald-50" : "text-slate-600 hover:text-emerald-800"}`;

const AccountButton = ({ mobile }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const p = mobile ? "mobile-nav" : "nav";
  if (!user) return (
    <Button asChild className="bg-emerald-800 hover:bg-emerald-900 rounded-full px-5" data-testid={`${p}-login-btn`}>
      <Link to="/login"><LogIn className="h-4 w-4 mr-2" />Masuk</Link>
    </Button>
  );
  return (
    <div className={`flex gap-2 ${mobile ? "flex-col items-start" : "items-center"}`}>
      <Button asChild className="bg-emerald-800 hover:bg-emerald-900 rounded-full px-5" data-testid={`${p}-dashboard-btn`}>
        <Link to={ROLE_HOME[user.role]}><LayoutDashboard className="h-4 w-4 mr-2" />Dashboard</Link>
      </Button>
      <Button variant="outline" className="rounded-full px-5 border-emerald-800 text-emerald-800" data-testid={`${p}-logout-btn`}
        onClick={async () => { await logout(); nav("/login"); }}>
        <LogOut className="h-4 w-4 mr-2" />Keluar / Ganti Akun
      </Button>
    </div>
  );
};

const PublicHeader = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/90 border-b border-slate-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden xl:flex items-center gap-1" data-testid="public-nav">
          {PUBLIC_NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={linkCls} data-testid={`nav-${n.id}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden xl:block"><AccountButton /></div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="xl:hidden p-2 rounded-md hover:bg-slate-100" aria-label="Buka menu" data-testid="mobile-menu-btn">
              <Menu className="h-6 w-6 text-slate-800" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-72">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <nav className="flex flex-col gap-1 mt-8 overflow-y-auto max-h-[calc(100vh-4rem)]" data-testid="mobile-public-nav">
              {MOBILE_GROUPS.map(([title, items]) => (
                <div key={title} className="mb-3 flex flex-col" data-testid={`mobile-group-${title.toLowerCase()}`}>
                  <p className="px-2.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">{title}</p>
                  {items.map((n) => (
                    <NavLink key={n.id} to={n.to} end={n.end} onClick={() => setOpen(false)} className={linkCls} data-testid={`mobile-nav-${n.id}`}>{n.label}</NavLink>
                  ))}
                </div>
              ))}
              <p className="px-2.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">Lainnya</p>
              <div className="mt-1" onClick={() => setOpen(false)}><AccountButton mobile /></div>
              <div className="mt-4 rounded-xl bg-emerald-50 p-3" data-testid="mobile-contact-sji">
                <p className="text-sm font-bold text-slate-900">Butuh Informasi? Hubungi PT SJI</p>
                <p className="text-xs text-slate-600 mb-2">WhatsApp: <span className="font-mono font-semibold">{SJI_WA_DISPLAY}</span></p>
                <WaButton testid="mobile-contact-wa-btn" className="w-full" />
              </div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

const PublicFooter = () => (
  <footer className="bg-emerald-950 text-emerald-50/80" data-testid="public-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
      <div className="space-y-4">
        <Logo light testid="footer-logo" />
        <p className="text-sm leading-relaxed max-w-xs">
          Platform Pengembangan & Monitoring Kebun Kopi. Mengubah lahan kosong atau belum dikelola milik Kelompok Tani/Koperasi maupun perorangan menjadi kebun kopi produktif.
        </p>
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-300 mb-4">Navigasi</p>
        <ul className="grid grid-cols-2 gap-2 text-sm">
          {PUBLIC_NAV.map((n) => (
            <li key={n.to}><Link to={n.to} className="hover:text-white transition-colors" data-testid={`footer-nav-${n.id}`}>{n.label}</Link></li>
          ))}
        </ul>
      </div>
      <div className="space-y-2 text-sm">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-300 mb-4">Kepemilikan</p>
        <p className="font-semibold text-white">Powered & Controlled by PT SJI</p>
        <p>PT SJI adalah pemilik platform, pemantau, quality support, dan mitra dukungan offtake kopi. Kebun dikelola Kelompok Tani/Koperasi atau Pengelola Perorangan terverifikasi.</p>
      </div>
      <div className="space-y-2 text-sm" data-testid="footer-contact-sji">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-300 mb-4">Kontak PT SJI</p>
        <p className="font-semibold text-white">Butuh Informasi? Hubungi PT SJI</p>
        <p>WhatsApp: <a href={SJI_WA_LINK} target="_blank" rel="noopener noreferrer" className="font-mono font-semibold text-white underline underline-offset-2" data-testid="footer-contact-number">{SJI_WA_DISPLAY}</a></p>
        <WaButton testid="footer-contact-wa-btn" className="mt-2" />
      </div>
    </div>
    <div className="border-t border-white/10 py-5 text-center text-xs text-emerald-100/60">
      © {new Date().getFullYear()} Salimbado Farm · PT SJI · <Link to="/kebijakan-privasi" className="underline hover:text-white" data-testid="footer-privacy-link">Kebijakan Privasi</Link> · <Link to="/syarat-penggunaan" className="underline hover:text-white" data-testid="footer-terms-link">Syarat Penggunaan</Link>
    </div>
  </footer>
);

export default function PublicLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAF6]">
      <PublicHeader />
      <main className="flex-1"><Outlet /></main>
      <PublicFooter />
    </div>
  );
}
